import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { desktopBridge, deviceLabel, iosPush, listen, nativeDeviceId, nativeShell, registerApns, setNativeDeviceId, type NativeNotification } from '@/lib/app-install';

/** Nur eigene Pfade öffnen (kein Sprung auf fremde Adressen aus Benachrichtigungen) */
const safePath = (url: unknown) => (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : '/');

/**
 * Verbindet die Oberfläche mit der nativen Hülle:
 * Windows-Programm – Live-Verbindung zum Portal, Benachrichtigungen über Windows, Klick öffnet den Vorgang;
 * iPhone/iPad – Tipp auf eine Mitteilung öffnet den Vorgang, Gerätetoken wird beim Start aufgefrischt.
 */
export function NativeBridge() {
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState(nativeDeviceId);
  useEffect(() => {
    const update = () => setDeviceId(nativeDeviceId());
    window.addEventListener('sp-native-device', update);
    return () => window.removeEventListener('sp-native-device', update);
  }, []);

  // Windows: Klick auf eine Benachrichtigung
  useEffect(() => desktopBridge()?.onNavigate((url) => navigate(safePath(url))), [navigate]);

  // Windows: Live-Verbindung
  useEffect(() => {
    const bridge = desktopBridge();
    if (!bridge || !deviceId) return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const connect = () => {
      source = new EventSource(`/api/push/stream/${deviceId}`, { withCredentials: true });
      source.addEventListener('notification', (e) => {
        try {
          const n = JSON.parse((e as MessageEvent<string>).data) as NativeNotification;
          bridge.notify({ ...n, url: safePath(n.url) });
        } catch {
          /* ungültige Nachricht ignorieren */
        }
      });
      source.onerror = () => {
        if (source?.readyState !== EventSource.CLOSED || stopped) return; // Browser verbindet selbst neu
        // Verbindung abgelehnt: Gerät entfernt oder abgemeldet?
        void api<{ devices: { id: string }[] }>('/push/config')
          .then((c) => {
            if (!c.devices.some((d) => d.id === deviceId)) setNativeDeviceId(null);
            else retry = setTimeout(connect, 30_000);
          })
          .catch(() => (retry = setTimeout(connect, 30_000)));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      source?.close();
    };
  }, [deviceId]);

  // iPhone/iPad: Tipp auf eine Mitteilung; Token auffrischen (Apple kann es ändern)
  useEffect(() => {
    const push = iosPush();
    if (!push) return;
    const handle = listen<{ notification: { data?: { url?: string } } }>(push, 'pushNotificationActionPerformed', (e) => navigate(safePath(e.notification.data?.url)));
    if (nativeShell() === 'ios' && nativeDeviceId()) {
      void push.checkPermissions().then(async (p) => {
        if (p.receive !== 'granted') return;
        const token = await registerApns();
        const r = await api<{ id: string }>('/push/native', { method: 'POST', body: { platform: 'apns', token, device: deviceLabel() } });
        setNativeDeviceId(r.id);
      }).catch(() => undefined);
    }
    return () => void handle.then((h) => h.remove());
  }, [navigate]);

  return null;
}
